/**
 * Visit Tracking Service
 * Automatically tracks page visits for business intelligence analytics
 */

let visitLogged = false; // Prevent duplicate visits on same page
let retryCount = 0;
const MAX_RETRIES = 3;

/**
 * Get CSRF token from meta tag or cookie
 */
function getCsrfToken() {
  // Try meta tag first
  const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (metaToken) return metaToken;
  
  // Try Laravel session cookie as fallback
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value);
    }
  }
  
  return null;
}

/**
 * Log a page visit to the backend
 */
export function logVisit() {
  // Prevent duplicate logging on same page
  if (visitLogged) return;
  
  // Don't track admin pages, API calls, or non-page visits
  if (window.location.pathname.includes('/admin') || 
      window.location.pathname.includes('/api/') ||
      window.location.pathname.includes('/stats/dashboard')) {
    return;
  }

  // Check if we're in an embedded context (iframe)
  const isEmbedded = window !== window.top;
  
  try {
    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      // In embedded contexts, CSRF tokens may not be available - this is normal
      if (isEmbedded) {
        // Silently skip tracking for embedded contexts
        return;
      } else {
        console.warn('No CSRF token found, skipping visit tracking');
        return;
      }
    }

    const data = {
      page_url: window.location.href,
      referrer: document.referrer || null,
      timestamp: new Date().toISOString(),
      is_embedded: isEmbedded
    };

    // Use fetch API for simple POST request
    fetch('/api/log-visit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': csrfToken
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (response.ok) {
        visitLogged = true;
        retryCount = 0;
        console.log('Visit logged successfully');
      } else if (response.status === 429) {
        console.warn('Visit tracking rate limited');
      } else if (response.status === 422) {
        console.warn('Invalid visit data');
      } else if (response.status === 419 && retryCount < MAX_RETRIES) {
        // CSRF token expired, try to refresh page to get new token
        console.warn('CSRF token expired, refreshing...');
        retryCount++;
        setTimeout(() => window.location.reload(), 1000);
      } else if (response.status === 500) {
        // Server error - silently log and continue (don't break user experience)
        console.warn('Visit tracking temporarily unavailable (server error)');
        visitLogged = true; // Prevent retries
      } else {
        console.warn('Failed to log visit:', response.status);
      }
    })
    .catch(error => {
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.warn(`Visit tracking error (retry ${retryCount}):`, error.message);
        setTimeout(logVisit, 2000); // Retry after 2 seconds
      } else {
        console.warn('Visit tracking failed after retries - continuing normally:', error.message);
        visitLogged = true; // Prevent further attempts
      }
    });

  } catch (error) {
    console.warn('Visit tracking error:', error);
  }
}

/**
 * Initialize visit tracking
 * Call this when the page loads
 */
export function initVisitTracking() {
  // Only track on production domains
  const allowedHosts = ['insuranceexpress.com', 'www.insuranceexpress.com', 'localhost'];
  if (!allowedHosts.includes(window.location.hostname)) {
    return;
  }

  // Log visit when page is fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', logVisit);
  } else {
    // DOM already loaded, wait a bit for CSRF token to be available
    setTimeout(logVisit, 500);
  }
}

// Auto-initialize if this script is loaded directly
if (typeof window !== 'undefined') {
  initVisitTracking();
} 